import type { ApiClient } from '../../../shared/api/api-client';

export type OrgClientSummary = {
  id: number;
  email: string;
  full_name: string;
  phone?: string | null;
  role: string;
  avatar_url?: string | null;
  email_verified?: boolean;
  organization_name?: string | null;
  /** RFC3339 UTC; null — неизвестно. */
  created_at?: string | null;
  last_login_at?: string | null;
  /** Как создан: self|admin|trainer|superadmin|platform_staff|tenant_request|yclients. */
  created_via?: string | null;
  created_by_user_id?: number | null;
  created_by_name?: string | null;
  /** RFC3339 UTC; когда клиент появился в CRM (первое появление в системе). */
  crm_joined_at?: string | null;
  /** RFC3339 UTC; когда клиент присоединился к текущей организации. */
  org_joined_at?: string | null;
  /** true — клиент зарегистрирован ранее в другой организации CRM; `created_by_*` замаскированы. */
  origin_other_org?: boolean;
  managed_by?: 'crm' | 'yclients' | null;
  is_activated?: boolean;
};

export type OrgClientsListResponse = {
  items: OrgClientSummary[];
  total: number;
};

export type OrgClientsListParams = {
  q?: string;
  /** Размер страницы (core: макс. 200). */
  limit?: number;
  /** Смещение постраничной выборки (`GET /admin/clients?offset=`). */
  offset?: number;
  /** Фильтр: клиенты с неотменённой записью в филиале (игнорируется, если RBAC уже зафиксировал scope филиала). */
  branch_id?: number | null;
  /** Явный контекст орг. для СА (`?organization_id=`) при имперсонации / пустом `organization_id` в JWT. */
  organization_id?: number | null;
  /** Фильтр по статусу подтверждения email: true — подтверждён, false — не подтверждён. */
  email_verified?: boolean;
  /** Фильтр по статусу активации (например 'yc_unactivated'). */
  activation_status?: string;
};

export type OrgClientScopedParams = {
  organization_id?: number | null;
};

export type OrgClientSearchSuggestionsParams = OrgClientScopedParams & {
  q?: string;
};

export type OrgClientByIdParams = OrgClientScopedParams & {
  clientId: number;
};

export type OrgClientMinor = {
  id: number;
  /** Имя ребёнка: одно или два слова. */
  name: string;
  date_of_birth?: string | null;
  consent_version?: number;
  consent_accepted_at?: string | null;
  /** client_app | staff_offline | legacy */
  consent_source?: string | null;
};

export type OrgClientCreateStatus = 'created' | 'linked' | 'exists';

/** Создание клиента сотрудником: email/пароль клиент задаёт сам при первом входе. */
export type OrgClientCreatePayload = {
  full_name: string;
  phone: string;
  is_active?: boolean;
};

export type OrgClientCreateResponse = OrgClientSummary & { status?: OrgClientCreateStatus };

export type OrgClientBulkItem = {
  row: number;
  full_name: string;
  phone: string;
};

export type OrgClientBulkItemStatus = 'create' | 'link' | 'exists' | 'error';

export type OrgClientBulkResultItem = {
  row: number;
  phone: string;
  full_name: string;
  status: OrgClientBulkItemStatus;
  client_id?: number;
  /** Код ошибки строки: invalid_phone | full_name_required | duplicate_in_file | invalid_row | internal. */
  code?: string;
  detail?: string;
};

export type OrgClientBulkSummary = {
  total: number;
  create: number;
  link: number;
  exists: number;
  errors: number;
};

export type OrgClientBulkPayload = {
  dry_run: boolean;
  /** Не более 500 строк за запрос. */
  items: OrgClientBulkItem[];
};

export type OrgClientBulkResponse = {
  dry_run: boolean;
  summary: OrgClientBulkSummary;
  items: OrgClientBulkResultItem[];
};

export type OrgClientUpdatePayload = {
  email?: string;
  full_name?: string;
  phone?: string | null;
  is_active?: boolean;
  /** Новый пароль; не передавать или пусто — не менять. */
  password?: string;
  avatar_url?: string | null;
  /** Роль в organization_memberships: client | trainer | staff */
  role?: string;
  /** Ручное подтверждение / сброс email (true → verified_at = now, false → null). */
  email_verified?: boolean;
};

export type OrgClientMinorCreatePayload = {
  name: string;
  date_of_birth: string;
  /** Подтверждение сотрудника: законный представитель подписал согласие на обработку данных ребёнка. */
  consent_confirmed: boolean;
};

export type OrgClientMinorUpdatePayload = {
  name?: string;
  date_of_birth?: string;
};

export type OrgClientAuditEntry = {
  id: number;
  created_at: string;
  action: string;
  action_display_ru?: string | null;
  actor_user_id: number;
  actor_role: string;
  entity_type: string;
  entity_id: number | null;
  organization_id: number | null;
  branch_id: number | null;
  details: unknown;
  entity_display_name?: string | null;
};

function appendOrganizationId(sp: URLSearchParams, organization_id?: number | null): void {
  if (organization_id != null && organization_id > 0) {
    sp.set('organization_id', String(organization_id));
  }
}

function buildClientsQuery(params: OrgClientsListParams): string {
  const sp = new URLSearchParams();
  if (params.q?.trim()) sp.set('q', params.q.trim());
  if (params.limit != null && params.limit > 0) sp.set('limit', String(params.limit));
  if (params.offset != null && params.offset > 0) sp.set('offset', String(params.offset));
  if (params.branch_id != null && params.branch_id > 0) sp.set('branch_id', String(params.branch_id));
  if (params.email_verified != null) sp.set('email_verified', String(params.email_verified));
  if (params.activation_status?.trim()) sp.set('activation_status', params.activation_status.trim());
  appendOrganizationId(sp, params.organization_id);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

function buildOrgContextQuery(organization_id?: number | null): string {
  const sp = new URLSearchParams();
  appendOrganizationId(sp, organization_id);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

function extractOrgClientsListItems(data: unknown): OrgClientSummary[] {
  if (Array.isArray(data)) return data as OrgClientSummary[];
  const obj = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  return Array.isArray(obj.items) ? (obj.items as OrgClientSummary[]) : [];
}

export async function fetchOrgClientsList(
  client: ApiClient,
  params: OrgClientsListParams = {}
): Promise<OrgClientsListResponse> {
  const q = buildClientsQuery({
    q: params.q,
    limit: params.limit ?? 20,
    offset: params.offset,
    branch_id: params.branch_id,
    organization_id: params.organization_id
  });
  const { data, total } = await client.requestJsonWithTotalCount<unknown>(`/admin/clients${q}`, { method: 'GET' });
  return { items: extractOrgClientsListItems(data), total };
}

/** Фактический ответ Go: `{ suggestions: string[] }`; легаси-массив — на всякий случай. */
export function normalizeOrgClientSearchSuggestions(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((x): x is string => typeof x === 'string');
  }
  const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  if (!obj || !Array.isArray(obj.suggestions)) {
    return [];
  }
  return obj.suggestions.filter((x): x is string => typeof x === 'string');
}

export async function fetchOrgClientSearchSuggestions(
  client: ApiClient,
  params: OrgClientSearchSuggestionsParams
): Promise<string[]> {
  const sp = new URLSearchParams();
  if (params.q?.trim()) sp.set('q', params.q.trim());
  appendOrganizationId(sp, params.organization_id);
  const qs = sp.toString() ? `?${sp.toString()}` : '';
  const raw = await client.request<unknown>(`/admin/clients/search-suggestions${qs}`, { method: 'GET' });
  return normalizeOrgClientSearchSuggestions(raw);
}

export async function fetchOrgClientDetail(client: ApiClient, params: OrgClientByIdParams): Promise<OrgClientSummary> {
  const qs = buildOrgContextQuery(params.organization_id);
  return client.request<OrgClientSummary>(`/admin/clients/${params.clientId}${qs}`, { method: 'GET' });
}

function normalizeOrgClientMinors(raw: unknown): OrgClientMinor[] {
  const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  if (!obj || !Array.isArray(obj.items)) {
    return [];
  }
  return obj.items.filter((row): row is OrgClientMinor => {
    if (!row || typeof row !== 'object') return false;
    const r = row as Record<string, unknown>;
    return typeof r.id === 'number' && typeof r.name === 'string';
  });
}

export async function fetchOrgClientMinors(client: ApiClient, params: OrgClientByIdParams): Promise<OrgClientMinor[]> {
  const qs = buildOrgContextQuery(params.organization_id);
  const raw = await client.request<unknown>(`/admin/clients/${params.clientId}/minors${qs}`, { method: 'GET' });
  return normalizeOrgClientMinors(raw);
}

function normalizeOrgClientAuditItems(raw: unknown): OrgClientAuditEntry[] {
  const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  if (!obj || !Array.isArray(obj.items)) {
    return [];
  }
  return obj.items.filter((row): row is OrgClientAuditEntry => {
    if (!row || typeof row !== 'object') return false;
    const r = row as Record<string, unknown>;
    const entityId = r.entity_id;
    const orgId = r.organization_id;
    const brId = r.branch_id;
    return (
      typeof r.id === 'number' &&
      typeof r.created_at === 'string' &&
      typeof r.action === 'string' &&
      typeof r.actor_user_id === 'number' &&
      typeof r.actor_role === 'string' &&
      typeof r.entity_type === 'string' &&
      (entityId === null || typeof entityId === 'number') &&
      (orgId === null || typeof orgId === 'number') &&
      (brId === null || typeof brId === 'number')
    );
  });
}

export type OrgClientAuditPageParams = OrgClientByIdParams & {
  limit?: number;
  offset?: number;
};

export async function fetchOrgClientAuditLogPage(
  client: ApiClient,
  params: OrgClientAuditPageParams
): Promise<{ items: OrgClientAuditEntry[]; total: number }> {
  const sp = new URLSearchParams();
  appendOrganizationId(sp, params.organization_id);
  const limit = params.limit ?? 20;
  const offset = params.offset ?? 0;
  if (limit > 0) sp.set('limit', String(limit));
  if (offset >= 0) sp.set('offset', String(offset));
  const qs = sp.toString() ? `?${sp.toString()}` : '';
  const { data, total } = await client.requestJsonWithTotalCount<unknown>(
    `/admin/clients/${params.clientId}/audit-log${qs}`,
    { method: 'GET' }
  );
  return { items: normalizeOrgClientAuditItems(data), total };
}

export async function createClient(
  client: ApiClient,
  payload: OrgClientCreatePayload,
  organization_id?: number | null
): Promise<OrgClientCreateResponse> {
  const qs = buildOrgContextQuery(organization_id);
  return client.request<OrgClientCreateResponse>(`/admin/clients${qs}`, {
    method: 'POST',
    body: payload
  });
}

/** Групповое добавление: `dry_run` — предпроверка без создания (≤ 500 строк). */
export async function bulkCreateClients(
  client: ApiClient,
  payload: OrgClientBulkPayload,
  organization_id?: number | null
): Promise<OrgClientBulkResponse> {
  const qs = buildOrgContextQuery(organization_id);
  return client.request<OrgClientBulkResponse>(`/admin/clients/bulk${qs}`, {
    method: 'POST',
    body: payload
  });
}

export async function updateClient(
  client: ApiClient,
  clientId: number,
  payload: OrgClientUpdatePayload,
  organization_id?: number | null
): Promise<OrgClientSummary> {
  const qs = buildOrgContextQuery(organization_id);
  return client.request<OrgClientSummary>(`/admin/clients/${clientId}${qs}`, {
    method: 'PATCH',
    body: payload
  });
}

export async function deleteClient(
  client: ApiClient,
  clientId: number,
  organization_id?: number | null
): Promise<void> {
  const qs = buildOrgContextQuery(organization_id);
  return client.request<void>(`/admin/clients/${clientId}${qs}`, {
    method: 'DELETE'
  });
}

export async function createClientMinor(
  client: ApiClient,
  clientId: number,
  payload: OrgClientMinorCreatePayload,
  organization_id?: number | null
): Promise<OrgClientMinor> {
  const qs = buildOrgContextQuery(organization_id);
  return client.request<OrgClientMinor>(`/admin/clients/${clientId}/minors${qs}`, {
    method: 'POST',
    body: payload
  });
}

export async function updateClientMinor(
  client: ApiClient,
  clientId: number,
  minorId: number,
  payload: OrgClientMinorUpdatePayload,
  organization_id?: number | null
): Promise<OrgClientMinor> {
  const qs = buildOrgContextQuery(organization_id);
  return client.request<OrgClientMinor>(`/admin/clients/${clientId}/minors/${minorId}${qs}`, {
    method: 'PATCH',
    body: payload
  });
}

export async function deleteClientMinor(
  client: ApiClient,
  clientId: number,
  minorId: number,
  organization_id?: number | null
): Promise<void> {
  const qs = buildOrgContextQuery(organization_id);
  return client.request<void>(`/admin/clients/${clientId}/minors/${minorId}${qs}`, {
    method: 'DELETE'
  });
}

export async function requestEmailVerificationForUser(
  client: ApiClient,
  userId: number
): Promise<void> {
  return client.request<void>('/auth/request-email-verification', {
    method: 'POST',
    body: { user_id: userId }
  });
}
